import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronDown, History as HistoryIcon, RefreshCcw } from 'lucide-react';
import { ItemFormModal, type ItemFormData } from '@/pages/review/ItemFormModal';
import { TaskEventDetailModal } from '@/components/domain/shared/TaskEventDetailModal';
import { DateStrip } from '@/components/domain/mobile/DateStrip';
import { MiniCalendar } from '@/components/domain/mobile/MiniCalendar';
import { TaskCard } from '@/components/domain/shared/TaskCard';
import { EventCard } from '@/components/domain/shared/EventCard';
import { DateGroupHeading } from '@/components/domain/shared/DateGroupHeading';
import { ChildFilterPills } from '@/components/Layout/mobile/ChildFilterPills';
import { ScheduleTabSkeleton } from '@/components/states/PageSkeletons';
import { ErrorState } from '@/components/states/ErrorState';
import { AddItemCard, IconButton } from '@/components/ui';
import { HamburgerMenuButton } from '@/components/Layout/mobile/HamburgerMenuButton';
import { scheduleService } from '@/services/schedule.service';
import { buildSeriesDelete } from '@/lib/seriesDelete';
import { seriesLabel } from '@/lib/recurrence';
import { buildSubtaskSummary, groupSubtasksByEvent } from '@/lib/subtasks';
import { getChildren, getMembers } from '@/services/household.service';
import type { HouseholdMemberWithUser } from '@/types';
import { useAppShell } from '@/hooks/useAppShell';
import { useHeroScrolled } from '@/hooks/useHeroScrolled';
import { useTaskCompletion } from '@/hooks/useTaskCompletion';
import { useChildList } from '@/hooks/useChildList';
import { useScheduleWindow } from '@/hooks/useScheduleWindow';
import { useInfiniteSchedule } from '@/hooks/useInfiniteSchedule';
import { useOngoingEvents } from '@/hooks/useOngoingEvents';
import { buildOngoingEvents, buildSpanBars } from '@/lib/ongoing';
import { OngoingSection } from '@/components/domain/mobile/OngoingSection';
import { SpanBarGroup } from '@/components/domain/mobile/SpanBarGroup';
import { useTodayKey } from '@/hooks/useTodayKey';
import { usePermissions } from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import { toIsoWithTz } from '@/lib/tz';
import { parseDateKey } from '@/lib/dateKey';
import {
  buildDatesWithItems,
  buildMultiDayFeed,
  getChildrenFromTask,
  getChildrenFromEvent,
  getMembersFromTask,
  getMembersFromEvent,
  getSourceSubject,
  taskToReviewItem,
  eventToReviewItem,
  type TaskRow,
  type EventRow,
} from '@/lib/schedule';
import type { Child } from '@/types';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Height of the sticky header stack (hamburger row + month/Today row +
 * DateStrip + divider) when expanded.
 *
 * This is load-bearing in TWO places that have to agree: it is the
 * `scroll-margin-top` that keeps a jumped-to day's title just under the
 * header, AND the top edge of the IntersectionObserver band that decides
 * which month the header names. They drifted apart before — YAR-257 grew
 * the row by 8px and only the scroll margin followed (148 → 156) — so
 * they read the one constant rather than each carrying their own copy.
 */
const STICKY_HEADER_OFFSET_PX = 156;

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function ScheduleTab() {
  const context = useAppShell();
  const { householdId } = context;
  const userTimezone = useUserStore((s) => s.timezone);
  // Live "today" in the profile timezone — re-renders at midnight so the
  // feed's past/today classification and its backward floor can't go
  // stale in a long-lived tab (YAR-132 AC #9 follow-up).
  const todayKey = useTodayKey(userTimezone);
  // Local-calendar Date for the profile-tz today. visibleDays are local
  // calendar dates keyed by their local Y-M-D, so the feed floor must be
  // the local date whose key equals todayKey.
  const todayFloor = useMemo(() => {
    const [y, m, d] = todayKey.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [todayKey]);
  const { canEdit, canComplete, canDelete } = usePermissions();
  const navigate = useNavigate();
  const [heroRef, isScrolled] = useHeroScrolled();

  // The sticky header's real height, measured rather than assumed.
  //
  // `STICKY_HEADER_OFFSET_PX` is the seed and the fallback, but the header is
  // no longer a fixed stack: the span bars render inside it and add a row (or
  // two, or an expanded group). It is load-bearing in two places that have to
  // agree — the jumped-to day's `scroll-margin-top` and the top edge of the
  // month observer's band — and YAR-409 is what happens when they disagree.
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [stickyOffset, setStickyOffset] = useState(STICKY_HEADER_OFFSET_PX);
  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      // A collapsed/unmounted header measures 0; keep the last good value
      // rather than anchoring day sections to the top of the viewport.
      if (h > 0) setStickyOffset(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // `visibleMonth` drives the sticky header's month label as the user
  // scrolls the day feed, AND is what we hand to DateStrip in `mode='month'`
  // contexts (currently unused — DateStrip is always rendered in `'week'`
  // mode which ignores `month`, so this is just a forward-compatible
  // anchor). Phase 5 consolidated the previous `currentMonth` /
  // `visibleMonth` pair: they served the same role except `visibleMonth`
  // was the one updated by the IntersectionObserver. We now use this single
  // source of truth and update it eagerly in `handleDateSelect` /
  // `handleTodayPress` so the header label doesn't flash the stale month
  // before the IO catches up.
  // Seeded from `?date=` when arriving from an SMS digest link (`/s/:date`
  // redirects here); today otherwise. Both `visibleMonth` and
  // `selectedDate` seed from the same value so the sticky header doesn't
  // flash the current month before correcting itself.
  //
  // Clamped up to `todayFloor` when the parsed date is in the past: the
  // digest link points at TOMORROW, but a link opened a few days late
  // would otherwise seed a past `selectedDate` while the infinite-scroll
  // feed below (floored at `minDate: todayFloor`) can never render
  // anything before today — the DateStrip would highlight a week the
  // feed never shows. Landing on today for a stale link is acceptable;
  // seeding a date the feed can't display is not.
  const seededDate = (() => {
    const parsed = parseDateKey(
      new URLSearchParams(window.location.search).get('date'),
    );
    if (!parsed) return null;
    return parsed.getTime() < todayFloor.getTime() ? todayFloor : parsed;
  })();
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const base = seededDate ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(
    () => seededDate ?? new Date(),
  );
  // Latch a date we need to scroll into view AFTER `visibleDays` updates.
  // Used by `handleDateSelect` / `handleTodayPress` when the target day
  // is outside the loaded window: we call `jumpTo`, which replaces
  // `visibleDays` on next render, and the effect below picks up the
  // pending key and calls `scrollIntoView` once the section exists.
  const pendingScrollKeyRef = useRef<string | null>(null);
  // While a programmatic scroll is in flight (click on a date / Today
  // button), the IntersectionObserver below is briefly suppressed from
  // updating EITHER `selectedDate` or `visibleMonth`. Otherwise sections
  // passing through the viewport mid-animation would flicker the
  // DateStrip highlight and the header's month label before settling on
  // the target. The month label used to be exempt from this guard, which
  // is YAR-409: every jump whose landing midpoint sits in an adjacent
  // month (the last day of a month being the easiest to reproduce) had
  // its label written by intersections the jump itself caused. Cleared
  // on a short timer after the scroll is kicked off — `scrollend` is not
  // yet universally supported.
  const suppressIoUpdatesRef = useRef(false);
  // Timer handle for the flag above, so back-to-back jumps can't have
  // the first jump's timer un-suppress the second jump mid-animation.
  const suppressIoTimerRef = useRef<number | null>(null);
  // The scroll container hosting the day feed. If the app shell wraps
  // ScheduleTab in a flex column with no `overflow-y` (window scrolls),
  // `scrollContainerRef.current` may stay null — `useInfiniteSchedule`
  // falls back to `document.documentElement` and observes the viewport.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    tasks: windowTasks,
    events,
    isLoading: loading,
    error,
    reload,
    ensureRange,
    extendBack,
    extendForward,
  } = useScheduleWindow({
    householdId: householdId ?? '',
    initialDate: selectedDate,
    userTimezone,
  });

  /* ── Sub-task counts for the event cards (YAR-297) ──────────────────────
     Fetched by related-event id rather than derived from `tasks`, which only
     holds the loaded date window: a Friday event's sub-task can sit on the
     following Monday, so deriving would undercount it and — worse — the
     number would change as the user scrolls and the window grew. History
     fetches separately for the same reason. */
  const [subtasksByEvent, setSubtasksByEvent] = useState<
    Map<string, TaskRow[]>
  >(new Map());
  const eventIdsKey = events.map((e) => e.id).join(',');

  /* The refetch is an explicit callback, not effect-only. Completing a
     sub-task is exactly the mutation that changes these counts, and it
     changes no *event* id — so `eventIdsKey` is invariant across it and an
     effect keyed on the event list can never fire. The band kept its
     mount-time numbers until the user reloaded the page (YAR-297).
     Every handler that refreshes the feed now calls `refresh()` below,
     which drives this alongside the window's own `reload()`. */
  const subtaskFetchSeq = useRef(0);
  const reloadSubtasks = useCallback(async () => {
    // Stamp every fetch so a slow response can't overwrite a newer one:
    // the window changes as the user scrolls, and a scroll-triggered
    // fetch can overlap a mutation-triggered one.
    const seq = ++subtaskFetchSeq.current;
    if (!eventIdsKey || !householdId) {
      setSubtasksByEvent(new Map());
      return;
    }
    try {
      const related = (await scheduleService.getTasksByRelatedEventIds(
        householdId,
        eventIdsKey.split(','),
      )) as TaskRow[];
      if (seq === subtaskFetchSeq.current) {
        setSubtasksByEvent(groupSubtasksByEvent(related));
      }
    } catch {
      // Non-fatal: the cards simply render without their counter rather
      // than failing the whole feed over a decorative band.
      if (seq === subtaskFetchSeq.current) setSubtasksByEvent(new Map());
    }
  }, [eventIdsKey, householdId]);

  useEffect(() => {
    void reloadSubtasks();
  }, [reloadSubtasks]);

  // ── Ongoing multi-day events (YAR-419) ────────────────────────────────
  //
  // Fetched separately from the feed on purpose: every other event read is
  // bounded by the browsed window, which is exactly why a run that started
  // before it was invisible. This pool is unbounded forward and re-derives
  // at midnight through `todayKey`.
  const { events: ongoingPool, refresh: refreshOngoing } = useOngoingEvents(
    householdId,
    todayKey,
    userTimezone,
  );

  /* Refresh the feed, the sub-task counts and the ongoing pool together.
     Mutation handlers call this instead of `reload()` so none can be
     refreshed without the others — the first two used to drift, which is the
     whole of YAR-297, and the ongoing pool is a third query that would
     otherwise keep serving the rows it fetched on mount. */
  const refresh = useCallback(async () => {
    await Promise.all([reload(), reloadSubtasks(), refreshOngoing()]);
  }, [reload, reloadSubtasks, refreshOngoing]);
  const {
    visibleDays,
    topSentinelRef,
    bottomSentinelRef,
    isExtending,
    jumpTo,
  } = useInfiniteSchedule({
    anchorDate: selectedDate,
    // Render today as the first day on mount, and hard-floor the window
    // at today: the Schedule feed never scrolls into the past — the
    // backward limit is the present day, history lives in the History
    // page (YAR-132 AC #9). `minDate` tracks the live profile-tz
    // todayKey, so the floor advances at midnight.
    initialBackDays: 0,
    minDate: todayFloor,
    extendBack,
    extendForward,
    scrollContainerRef,
  });
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<
    { kind: 'task'; data: TaskRow } | { kind: 'event'; data: EventRow } | null
  >(null);
  const [detailItem, setDetailItem] = useState<
    { kind: 'task'; data: TaskRow } | { kind: 'event'; data: EventRow } | null
  >(null);

  // Open create modal when navigated with ?create=true (from sidebar).
  // VIEWER (view-only) members can't create items in someone else's
  // household, so ignore the param for them — defense-in-depth in case
  // the sidebar entry slips through. Also clears `?date=` once consumed
  // by the `selectedDate`/`visibleMonth` seeding above (from `/s/:date`
  // SMS digest links) so it doesn't linger in the URL after landing.
  useEffect(() => {
    const wantsCreate = searchParams.get('create') === 'true';
    const hasDate = searchParams.get('date') !== null;
    if (!wantsCreate && !hasDate) return;
    if (wantsCreate && canEdit) setShowAddModal(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, canEdit]);
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  const [householdChildren, setHouseholdChildren] = useState<Child[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<
    HouseholdMemberWithUser[]
  >([]);

  // The sticky header label tracks the month most visible in the viewport
  // while the USER scrolls — `visibleMonth` is updated by the
  // IntersectionObserver wired below — and the month of the target date
  // while a jump this component started is in flight.
  const monthLabel = `${MONTH_NAMES[visibleMonth.getMonth()]} ${visibleMonth.getFullYear()}`;

  useEffect(() => {
    if (!householdId) return;
    let cancelled = false;
    (async () => {
      try {
        const [c, m] = await Promise.all([
          getChildren(householdId),
          getMembers(householdId),
        ]);
        if (!cancelled) {
          setHouseholdChildren(c);
          setHouseholdMembers(m);
        }
      } catch (err) {
        console.error('Failed to load household data:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  // True iff `date` is one of the days currently rendered in the feed.
  // Used to decide between "scroll into view" (cheap, same window) and
  // "jumpTo" (resets the window around the new date).
  const isDateInVisibleDays = useCallback(
    (date: Date) => {
      const key = toDateKey(date);
      return visibleDays.some((d) => toDateKey(d) === key);
    },
    [visibleDays],
  );

  // Suppress IO-driven `selectedDate` / `visibleMonth` updates for
  // ~600ms after a programmatic scroll is kicked off. Smooth-scroll
  // animations are typically ~300-500ms; 600ms is a small safety margin.
  // Re-arming the timer on every call keeps a rapid second jump fully
  // covered instead of inheriting the first jump's remaining window.
  const beginProgrammaticScroll = useCallback(() => {
    suppressIoUpdatesRef.current = true;
    if (suppressIoTimerRef.current !== null) {
      window.clearTimeout(suppressIoTimerRef.current);
    }
    suppressIoTimerRef.current = window.setTimeout(() => {
      suppressIoTimerRef.current = null;
      suppressIoUpdatesRef.current = false;
    }, 600);
  }, []);

  useEffect(
    () => () => {
      if (suppressIoTimerRef.current !== null) {
        window.clearTimeout(suppressIoTimerRef.current);
      }
    },
    [],
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      const key = toDateKey(date);
      if (isDateInVisibleDays(date)) {
        // Same window — just scroll the existing section into view.
        // Align `visibleMonth` up front, exactly like the `jumpTo`
        // branch below and `handleTodayPress` already do. This branch
        // used to leave the label to the IntersectionObserver, which is
        // no longer allowed to speak during a programmatic scroll — and
        // the observer does not necessarily fire again once the scroll
        // settles (it only reports intersection *changes*), so relying
        // on it would strand the label on the previous month.
        setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
        beginProgrammaticScroll();
        const el = document.getElementById(`day-${key}`);
        el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        // Date is outside the loaded window. Re-center via `jumpTo`,
        // then scroll the target section into view once it mounts.
        pendingScrollKeyRef.current = key;
        // Also align `visibleMonth` immediately so the header label
        // doesn't briefly show the stale month before the IO catches up.
        setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
        jumpTo(date);
      }
    },
    [isDateInVisibleDays, jumpTo, beginProgrammaticScroll],
  );

  const handleTodayPress = useCallback(() => {
    const now = new Date();
    setSelectedDate(now);
    // Re-center the infinite-scroll window around today. Without this
    // the feed would stay anchored to the original selectedDate from
    // mount, so "Today" would only update the highlighted date in the
    // strip — not the rendered day list.
    pendingScrollKeyRef.current = toDateKey(now);
    setVisibleMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    jumpTo(now);
  }, [jumpTo]);

  // After `jumpTo` swaps `visibleDays`, scroll the latched target day
  // into view on the next paint. useLayoutEffect so the scroll lands in
  // the same frame the new sections render — avoiding a one-frame
  // flicker at the top of the new window.
  //
  // Instant, not smooth: the window was just replaced, so there is no
  // continuity to animate over — and the hook may prepend the days
  // behind a future jump right away (YAR-470). A prepend restores the
  // scroll anchor by writing `scrollTop`, which aborts any smooth scroll
  // in flight and would leave the feed parked short of the tapped day.
  useLayoutEffect(() => {
    const key = pendingScrollKeyRef.current;
    if (!key) return;
    const el = document.getElementById(`day-${key}`);
    if (el) {
      beginProgrammaticScroll();
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
      pendingScrollKeyRef.current = null;
    }
  }, [visibleDays, beginProgrammaticScroll]);

  const selectedDateKey = toDateKey(selectedDate);

  // The nav's Today badge is counted by its own query in AppShell, which
  // cannot see a mutation made here. Every mutation re-counts it — in a
  // `finally`, so a failed feed refresh cannot leave the badge stale while
  // the write itself went through.
  const { refreshChrome } = context;
  const refreshAfterMutation = useCallback(async () => {
    try {
      await refresh();
    } finally {
      refreshChrome?.();
    }
  }, [refresh, refreshChrome]);

  const handleAddItem = useCallback(
    async (data: ItemFormData) => {
      if (!householdId) {
        throw new Error('Household not loaded yet. Please try again.');
      }
      const childIds = data.child_names
        .map((name) => householdChildren.find((c) => c.name === name)?.id)
        .filter((id): id is string => typeof id === 'string');

      const toIso = (dt: string) => toIsoWithTz(dt, userTimezone);

      if (data.type === 'task') {
        const raw = data.due_datetime ?? data.start_datetime ?? null;
        await scheduleService.createTask({
          title: data.title,
          due_date: raw ? toIso(raw) : null,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          notes: data.description ?? null,
          location: data.location ?? null,
          links: data.links,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
          // YAR-309: picking a Related Event here saved a task with no link,
          // so the chosen event never listed it under Sub-tasks.
          related_event_id: data.related_event_id,
          household_id: householdId,
        });
      } else {
        await scheduleService.createEvent({
          title: data.title,
          start_date: data.start_datetime
            ? toIso(data.start_datetime)
            : toIso(selectedDateKey),
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          location: data.location ?? null,
          links: data.links,
          notes: data.description ?? null,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
          household_id: householdId,
        });
      }

      setShowAddModal(false);
      await refreshAfterMutation();
    },
    [
      householdId,
      householdChildren,
      refreshAfterMutation,
      userTimezone,
      selectedDateKey,
    ],
  );

  const handleEditItem = useCallback(
    async (data: ItemFormData) => {
      if (!editingItem) return;
      const childIds = data.child_names
        .map((name) => householdChildren.find((c) => c.name === name)?.id)
        .filter((id): id is string => typeof id === 'string');
      const toIso = (dt: string) => toIsoWithTz(dt, userTimezone);

      if (editingItem.kind === 'task') {
        const raw = data.due_datetime ?? data.start_datetime ?? null;
        await scheduleService.updateTask(editingItem.data.id, {
          title: data.title,
          due_date: raw ? toIso(raw) : null,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          notes: data.description ?? null,
          location: data.location ?? null,
          links: data.links,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
          // YAR-309: the form has emitted this since YAR-295 wired up the
          // "Related Event" dropdown; every handleEditItem simply never
          // forwarded it, so clearing or changing the link was a no-op.
          related_event_id: data.related_event_id,
        });
      } else {
        await scheduleService.updateEvent(editingItem.data.id, {
          title: data.title,
          start_date: data.start_datetime
            ? toIso(data.start_datetime)
            : editingItem.data.start_date,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          location: data.location ?? null,
          links: data.links,
          notes: data.description ?? null,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
        });
      }

      setEditingItem(null);
      await refreshAfterMutation();
    },
    [editingItem, householdChildren, refreshAfterMutation, userTimezone],
  );

  // Completing a task (YAR-476): it leaves the list at once, the undo toast
  // comes up, and Undo writes back the status it had. The chrome re-counts
  // too, so the nav's Today badge follows a task completed here.
  const reloadAfterStatusChange = refreshAfterMutation;
  const completion = useTaskCompletion({
    onChanged: reloadAfterStatusChange,
  });
  const { applyOverrides } = completion;

  // The feed's tasks: optimistic statuses first, then DONE tasks dropped.
  // A completed task leaves Schedule the way it leaves Today — it used to
  // stay in place, checked. Its way back is History (YAR-476, YAR-176).
  const tasks = useMemo(
    () => applyOverrides(windowTasks).filter((t) => t.status !== 'DONE'),
    [applyOverrides, windowTasks],
  );

  /* Key describing the day sections that are actually IN THE DOM.
     `visibleDays` is NOT that: the feed is swapped for a skeleton while
     `loading`, and for an error panel while `error`, so on a cold mount
     `visibleDays` is fully populated a whole render BEFORE the first
     `[data-day-section]` exists. Keying the observer effect on
     `visibleDays` alone meant it ran once on mount, found zero sections,
     bailed, and never re-ran — because `visibleDays` doesn't change when
     the fetch resolves. The highlight then sat on the entry day for the
     entire initially-loaded window and only started tracking once the
     user scrolled far enough to extend it (which DOES change
     `visibleDays`). That is exactly YAR-384. Keying on this string
     instead re-attaches the observer on the loading→loaded and
     error→recovered transitions too, while still not churning on every
     data refresh (the day keys are unchanged by a reload). */
  const renderedDayKeys = useMemo(
    () => (loading || error ? '' : visibleDays.map(toDateKey).join(',')),
    [loading, error, visibleDays],
  );

  // Observe each rendered day section and update `visibleMonth` to the
  // month of whichever section is currently crossing the viewport's
  // vertical midpoint. `rootMargin: -50% / -50%` collapses the
  // observer root to a 1px horizontal band at the middle of the
  // viewport, so at most one section is "intersecting" at a time
  // (the one the user is reading). When that month changes we also
  // call `ensureRange` for the visible month so MiniCalendar dots
  // stay accurate without waiting for the user to open it.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    // Empty when there are no days, or when the feed isn't rendered yet
    // (skeleton / error panel) — in both cases there is nothing to observe.
    if (!renderedDayKeys) return;
    const sections =
      document.querySelectorAll<HTMLElement>('[data-day-section]');
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Ignore everything the observer reports while a programmatic
        // scroll is in flight. These entries are a consequence of a
        // scroll this component started, not of the user reading: the
        // sections streaming past mid-animation would flicker the
        // DateStrip highlight and the header's month label, and the
        // section sitting at the midpoint when the scroll lands is not
        // necessarily the one that was asked for (`scrollIntoView` puts
        // the target at the TOP, so a short target day leaves the
        // midpoint over the following day — and that day can belong to
        // the next month). Both handlers already set `selectedDate` and
        // `visibleMonth` to the target up front, so there is nothing
        // here worth listening to until the user scrolls again (YAR-409).
        if (suppressIoUpdatesRef.current) return;
        // Pick the first intersecting entry — under our rootMargin
        // collapse there should only ever be one at a time, but if
        // multiple slip through (e.g. very short sections), prefer
        // the one nearest the top.
        const hits = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const first = hits[0];
        if (!first) return;
        const monthAttr = (first.target as HTMLElement).dataset.month;
        if (monthAttr) {
          const [yStr, mStr] = monthAttr.split('-');
          const y = Number(yStr);
          const m = Number(mStr);
          if (Number.isFinite(y) && Number.isFinite(m)) {
            setVisibleMonth((prev) => {
              if (prev.getFullYear() === y && prev.getMonth() === m)
                return prev;
              return new Date(y, m, 1);
            });
          }
        }

        // Track the currently-viewed day so the DateStrip highlight
        // moves with the scroll. The day key lives on the section's
        // `id` ("day-YYYY-MM-DD"), set when the section is rendered.
        const id = (first.target as HTMLElement).id;
        if (!id.startsWith('day-')) return;
        const dayKey = id.slice(4);
        const [dyStr, dmStr, ddStr] = dayKey.split('-');
        const dy = Number(dyStr);
        const dm = Number(dmStr);
        const dd = Number(ddStr);
        if (
          !Number.isFinite(dy) ||
          !Number.isFinite(dm) ||
          !Number.isFinite(dd)
        )
          return;
        setSelectedDate((prev) => {
          if (
            prev.getFullYear() === dy &&
            prev.getMonth() === dm - 1 &&
            prev.getDate() === dd
          )
            return prev;
          return new Date(dy, dm - 1, dd);
        });
      },
      {
        // Probe the band that starts immediately under the sticky
        // header — NOT the viewport midpoint (YAR-409, second report).
        //
        // `scrollIntoView({block:'start'})` lands a jumped-to day at
        // `STICKY_HEADER_OFFSET_PX`, while the old `-50%/-50%` probed
        // the midpoint, half a screen lower. For a short day section
        // that midpoint sits over the FOLLOWING day — which is why
        // jumping to the last day of a month reproduced so reliably.
        // The 600ms suppression hid it only until the user moved:
        // an IntersectionObserver reports intersection *changes*, so
        // the "next day is intersecting" event fired during the
        // suppressed window and was dropped, then the first nudge of
        // the feed replayed the current state and flipped the label.
        //
        // Probing under the header makes the observer agree with where
        // the scroll actually lands, and makes the steady state
        // self-consistent too: the header now names the month of the
        // section directly beneath it, instead of being able to read
        // "September" above a section titled "August 31".
        //
        // The band is deliberately wide rather than a 1px line — the
        // handler already sorts hits by `boundingClientRect.top` and
        // takes the topmost, so a wide band costs nothing and avoids
        // collapsing to a negative height on short viewports.
        rootMargin: `-${stickyOffset}px 0px -40% 0px`,
        threshold: 0,
      },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
    // Re-observe whenever the rendered day set changes — prepend /
    // append / jumpTo, and also the first paint of the feed itself once
    // the initial fetch resolves — so newly mounted sections get picked up.
    // `stickyOffset` too: the band's top edge IS the header's height, so a
    // header that grew a span-bar row must re-arm the observer.
  }, [renderedDayKeys, stickyOffset]);

  // When the visible month changes, prefetch that month's data so the
  // MiniCalendar dots feel responsive. `ensureRange` is idempotent
  // against already-loaded ranges, so this is cheap on repeat visits.
  useEffect(() => {
    const start = toDateKey(
      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1),
    );
    const end = toDateKey(
      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0),
    );
    void ensureRange(start, end);
  }, [visibleMonth, ensureRange]);

  // Build a Set of date strings that have items, for the DateStrip + MiniCalendar dots.
  // Delegated to `buildDatesWithItems` so both pickers share the same filter
  // semantics (timezone bucketing + selectedChildId) as the day feed.
  //
  // The ongoing pool is merged in because the feed query only returns events
  // whose START falls inside the browsed window: a run that began earlier is
  // absent from `events` entirely, so neither its start nor its end day would
  // light a cell. Duplicates are free — the result is a Set of day keys.
  const datesWithItems = useMemo(
    () =>
      buildDatesWithItems(
        tasks,
        [...events, ...ongoingPool],
        userTimezone,
        selectedChildId,
      ),
    [tasks, events, ongoingPool, userTimezone, selectedChildId],
  );

  // Membership and order are global — the child filter re-scopes them
  // (AC 17), browsing another week never does (AC 16).
  const ongoingRows = useMemo(
    () =>
      buildOngoingEvents(ongoingPool, {
        todayKey,
        userTimezone,
        selectedChildId,
      }),
    [ongoingPool, todayKey, userTimezone, selectedChildId],
  );

  // The seven day keys the strip is showing, so the bars can be laid on the
  // same columns as the cells above them.
  const weekKeys = useMemo(() => {
    const weekStart = new Date(selectedDate);
    weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return toDateKey(d);
    });
  }, [selectedDate]);

  const spanBars = useMemo(
    () => buildSpanBars(ongoingRows, weekKeys),
    [ongoingRows, weekKeys],
  );

  const openEventDetail = useCallback((event: EventRow) => {
    setDetailItem({ kind: 'event', data: event });
  }, []);

  // Prefetch the visible month's data so dots populate as soon as the user
  // navigates the MiniCalendar to a previous/next month. ensureRange is
  // idempotent over already-loaded ranges, so paging twice into the same
  // month doesn't refetch.
  const handleMiniCalendarMonthChange = useCallback(
    (newMonth: Date) => {
      const start = toDateKey(
        new Date(newMonth.getFullYear(), newMonth.getMonth(), 1),
      );
      const end = toDateKey(
        new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0),
      );
      void ensureRange(start, end);
    },
    [ensureRange],
  );

  const childList = useChildList(tasks, events);

  // Multi-day feed, built from the infinite-scroll window. `visibleDays`
  // owns the rendered date list; `useScheduleWindow` owns the data behind
  // those days. The two grow in lockstep as the user scrolls.
  const dayGroups = useMemo(
    () =>
      buildMultiDayFeed(
        events,
        tasks,
        visibleDays,
        selectedChildId,
        userTimezone,
        // Live key so past/today re-buckets when midnight passes while
        // the tab stays mounted.
        todayKey,
      ),
    [events, tasks, visibleDays, selectedChildId, userTimezone, todayKey],
  );

  // Error state — hero + calendar + centered error
  if (error && !loading) {
    return (
      <div className="flex flex-col min-h-full bg-surface">
        <div className="p-5">
          {context?.onOpenDrawer && (
            <HamburgerMenuButton
              onClick={context.onOpenDrawer}
              showBadge={false}
            />
          )}
          <h1 className="text-[32px] font-bold leading-10 text-foreground mt-4">
            Schedule
          </h1>
        </div>
        <div className="px-5">
          <DateStrip
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            datesWithItems={datesWithItems}
            month={visibleMonth}
            mode="week"
            userTimezone={userTimezone}
          />
        </div>
        <ErrorState
          message="Unable to load items."
          onRetry={() => {
            // Retry both reads — the sub-task fetch swallows its own
            // errors, so a failed one is invisible until something asks
            // for it again.
            void refresh();
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Fixed sticky header — appears on scroll */}
      <div
        ref={stickyHeaderRef}
        className={[
          'fixed top-0 left-0 right-0 z-[100] bg-background transition-all duration-200',
          isScrolled
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-full pointer-events-none',
        ].join(' ')}
      >
        {/* Row 1: hamburger | centered "Schedule" | icons */}
        <div className="flex items-center justify-center px-5 py-2">
          <div className="flex-1 min-w-0">
            {context?.onOpenDrawer && (
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={false}
                variant="compact"
                className="-ml-2.5"
              />
            )}
          </div>
          <span className="text-xl font-bold leading-6 text-foreground text-center whitespace-nowrap">
            Schedule
          </span>
          <div className="flex-1 min-w-0 flex items-center justify-end">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="View history"
              onClick={() => navigate('/history')}
            >
              {/* 24px, not 18 (YAR-257 re-open). Figma's Schedule top nav
                  puts a 24x24 icon inside a 44x44 tap target
                  (`Button Group Item` → `Icon`, 10px padding); the app's
                  32px IconButton keeps its box so the header height is
                  unchanged, only the glyph grows. */}
              <HistoryIcon
                size={24}
                className="text-muted-foreground"
              />
            </IconButton>
          </div>
        </div>

        {/* Divider above the month/year row (YAR-257 — Figma flanks the whole
            calendar block, month label included; it used to sit between the
            month row and the week strip, so the month read as part of the
            header above rather than as the calendar's own label). */}
        <div className="h-px bg-border" />

        {/* Row 2: month selector | "Today" button.
            Figma also puts a 28x28 `list-filter` icon button to the right of
            "Today"; it is NOT implemented, because this app already ships an
            always-visible child filter (<ChildFilterPills> below the week
            strip) that the Figma frame does not have, so what the icon should
            do — and whether the pills stay — is an open design decision.
            See the YAR-257 hand-back. */}
        <div className="flex items-center justify-between px-5 pt-1 pb-1">
          <button
            type="button"
            onClick={() => setShowMiniCalendar((prev) => !prev)}
            className="flex items-center gap-1 text-sm font-semibold text-muted-foreground cursor-pointer bg-transparent border-none p-0"
          >
            {monthLabel}
            <ChevronDown size={14} />
          </button>
          {/* "Back to today" (YAR-257 re-open). Figma models this as the
              design system's secondary Button, not bare text: a 76x28 pill
              with a `border/neutral/default-subtle` (#e5e5e5 = `border`)
              1px outline, `surface/default` fill, 6px radius, 6px inline /
              4px block padding, a 6px gap and a 16px leading refresh glyph.
              The label is `text/neutral/default` (#5e5e5e =
              `muted-foreground`) — it was `text-primary` (#264133), which
              read as an accent action rather than a neutral control. */}
          <button
            type="button"
            onClick={handleTodayPress}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-surface px-1.5 py-1 text-sm font-semibold text-muted-foreground cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <RefreshCcw size={16} />
            Today
          </button>
        </div>

        {/* Row 3: DateStrip week calendar */}
        <div className="px-3">
          <DateStrip
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            datesWithItems={datesWithItems}
            month={visibleMonth}
            mode="week"
            userTimezone={userTimezone}
          />
        </div>

        {/* Span bars pinned under the week strip. Sticky-header only: they
            exist to keep a long event on screen once the "All ongoing"
            section above has scrolled away (YAR-419 AC 7). */}
        {spanBars.length > 0 && (
          <div className="px-5">
            <SpanBarGroup
              bars={spanBars}
              onSelect={openEventDetail}
            />
          </div>
        )}

        {/* Row 4: divider */}
        <div className="h-px bg-border" />

        {/* MiniCalendar dropdown (in sticky header). The wrapper gives the
            dropdown the same containing block the hero gives it — the 20px
            gutter — so it opens at the same width and left edge from both
            (YAR-420 re-open: it used to span the whole header here). */}
        {showMiniCalendar && (
          <div className="relative px-5">
            <MiniCalendar
              selectedDate={selectedDate}
              onDateSelect={(date) => {
                handleDateSelect(date);
                setShowMiniCalendar(false);
              }}
              onClose={() => setShowMiniCalendar(false)}
              datesWithItems={datesWithItems}
              onViewMonthChange={handleMiniCalendarMonthChange}
              userTimezone={userTimezone}
              ongoingRuns={ongoingRows}
            />
          </div>
        )}
      </div>

      {/* Scrollable content. The page (html/body) is what actually
          scrolls in production — `overflow-anchor: none` is applied
          globally in index.css so the browser's anchor heuristic can't
          fight `useInfiniteSchedule`'s manual scrollTop restore. */}
      <div
        ref={scrollContainerRef}
        className="p-5 flex flex-col gap-5 pb-24"
      >
        {/* Hero section */}
        <div ref={heroRef}>
          {context?.onOpenDrawer && (
            <div className="mb-4 flex items-center">
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={false}
              />
            </div>
          )}

          {/* Title row — the History icon sits on the SAME line as the
              "Schedule" title (YAR-257). It used to be stacked in the
              hamburger row above the title, which left it visually
              detached from the heading it belongs to. The hamburger keeps
              its own row and its current gutter alignment (meeting
              decision: the app's alignment stands, not Figma's). */}
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-[32px] font-bold leading-10 text-foreground">
              Schedule
            </h1>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="View history"
              onClick={() => navigate('/history')}
            >
              {/* 24px — same glyph size as the sticky header (YAR-257). */}
              <HistoryIcon
                size={24}
                className="text-muted-foreground"
              />
            </IconButton>
          </div>
        </div>

        {/* Divider above the month/year row (YAR-257 — same treatment the
            sticky header already got). Figma flanks the whole calendar
            block, month label included; the line used to sit between the
            month row and the week strip, so on landing the month read as
            part of the header above rather than as the calendar's label.
            `-mx-5` bleeds it past the p-5 gutter to the screen edges. */}
        <div className="-mx-5 h-px bg-border" />

        {/* Month selector with mini calendar */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMiniCalendar((prev) => !prev)}
            className="flex items-center gap-1 text-base font-semibold text-muted-foreground cursor-pointer bg-transparent border-none p-0"
          >
            {monthLabel}
            <ChevronDown size={16} />
          </button>

          {/* MiniCalendar dropdown (in hero). Same props as the sticky
              header's instance — `ongoingRuns` included, so the period band
              shows from the first open and not only once the header has
              taken over (YAR-420 re-open). */}
          {showMiniCalendar && !isScrolled && (
            <MiniCalendar
              selectedDate={selectedDate}
              onDateSelect={(date) => {
                handleDateSelect(date);
                setShowMiniCalendar(false);
              }}
              onClose={() => setShowMiniCalendar(false)}
              datesWithItems={datesWithItems}
              onViewMonthChange={handleMiniCalendarMonthChange}
              userTimezone={userTimezone}
              ongoingRuns={ongoingRows}
            />
          )}
        </div>

        {/* DateStrip week calendar — closes the calendar block opened by the
            divider above the month/year row (YAR-257), so only the BOTTOM
            border lives here. `-mx-5` bleeds the border past the p-5 gutter
            to the screen edges; `px-5` re-pads the strip to the content edge. */}
        <div className="-mx-5 border-b border-border px-5 py-2">
          <DateStrip
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            datesWithItems={datesWithItems}
            month={visibleMonth}
            mode="week"
            userTimezone={userTimezone}
          />
        </div>

        {/* Child filter pills */}
        {childList.length > 0 && (
          <ChildFilterPills
            childList={childList}
            selectedId={selectedChildId}
            onSelect={setSelectedChildId}
          />
        )}

        {/* Loading state */}
        {loading && <ScheduleTabSkeleton />}

        {/* All ongoing — sits above the first day heading. Hidden entirely at
            zero, and covered by the tab skeleton while loading (AC 19), so it
            carries no loading or empty state of its own. */}
        {!loading && !error && (
          <OngoingSection
            rows={ongoingRows}
            onSelect={openEventDetail}
          />
        )}

        {/* Multi-day feed */}
        {!loading && !error && (
          <div className="relative flex flex-col gap-6">
            {/* Top sentinel — triggers a prepend when scrolled into view,
                which is how the days between today and a date the user
                jumped to become reachable (YAR-470). Gated inside the hook
                so it doesn't fire on initial mount.
                Out of flow on purpose: an in-flow child costs a gap-6 of
                dead space above the first day title (YAR-257 S7), and the
                `empty:hidden` that used to hide it made it `display:none`
                — an element with no box never intersects, so the feed
                could never load backwards at all. The bottom live region
                announces "Loading more…" for both directions. */}
            <div
              ref={topSentinelRef}
              aria-hidden="true"
              aria-busy={isExtending}
              className="pointer-events-none absolute top-0 left-0 h-px w-px"
            />
            {dayGroups.map((group) => (
              <section
                key={group.dateKey}
                id={`day-${group.dateKey}`}
                data-day-section=""
                data-month={`${group.date.getFullYear()}-${group.date.getMonth()}`}
                // Keeps the targeted day's title visible just under the
                // sticky header when `scrollIntoView` lands, whether the
                // scroll came from clicking a strip date, pressing Today,
                // or selecting a MiniCalendar day. Inline rather than a
                // `scroll-mt-[...]` class so it reads the same constant the
                // month observer's band does — Tailwind cannot generate a
                // class from a variable, and these two silently disagreeing
                // is what YAR-409 was.
                style={{ scrollMarginTop: stickyOffset }}
              >
                <DateGroupHeading
                  date={group.date}
                  isToday={group.isToday}
                  isPast={group.isPast}
                />

                <div className="flex flex-col gap-3 mt-3">
                  {group.items.map((item) => {
                    if (item.type === 'event') {
                      const event = item.data;
                      return (
                        <EventCard
                          key={event.id}
                          id={event.id}
                          title={event.title}
                          description={event.notes ?? undefined}
                          startDate={event.start_date}
                          endDate={event.end_date ?? undefined}
                          isTimeUnknown={
                            event.source_review_item?.is_time_unknown ?? false
                          }
                          location={event.location ?? undefined}
                          link={event.link ?? undefined}
                          children={getChildrenFromEvent(event).map((c) => ({
                            name: c.name,
                            emoji: c.emoji_avatar ?? undefined,
                            color: c.emoji_color ?? undefined,
                            imagePath: c.avatar_image_path ?? undefined,
                          }))}
                          members={getMembersFromEvent(event).map((m) => ({
                            name: m.display_name ?? m.email ?? 'Member',
                            emoji: m.avatar_emoji ?? undefined,
                            color: m.avatar_color ?? undefined,
                            imagePath: m.avatar_image_path ?? undefined,
                          }))}
                          sourceSubject={getSourceSubject(event)}
                          repeatLabel={seriesLabel(event) ?? undefined}
                          subtaskSummary={buildSubtaskSummary(
                            subtasksByEvent.get(event.id),
                          )}
                          onClick={() =>
                            setDetailItem({ kind: 'event', data: event })
                          }
                          timezone={userTimezone}
                          muted={group.isPast}
                        />
                      );
                    }
                    const task = item.data;
                    return (
                      <TaskCard
                        key={task.id}
                        id={task.id}
                        title={task.title}
                        description={task.notes ?? undefined}
                        dueDate={task.due_date ?? undefined}
                        location={task.location ?? undefined}
                        link={task.link ?? undefined}
                        status={
                          task.status as
                            | 'TODO'
                            | 'IN_PROGRESS'
                            | 'DONE'
                            | 'CANCELLED'
                        }
                        children={getChildrenFromTask(task).map((c) => ({
                          name: c.name,
                          emoji: c.emoji_avatar ?? undefined,
                          color: c.emoji_color ?? undefined,
                          imagePath: c.avatar_image_path ?? undefined,
                        }))}
                        members={getMembersFromTask(task).map((m) => ({
                          name: m.display_name ?? m.email ?? 'Member',
                          emoji: m.avatar_emoji ?? undefined,
                          color: m.avatar_color ?? undefined,
                          imagePath: m.avatar_image_path ?? undefined,
                        }))}
                        sourceSubject={getSourceSubject(task)}
                        onToggleComplete={
                          canComplete
                            ? () => void completion.toggle(task)
                            : undefined
                        }
                        onClick={() =>
                          setDetailItem({ kind: 'task', data: task })
                        }
                        timezone={userTimezone}
                        muted={group.isPast}
                      />
                    );
                  })}
                  {group.items.length === 0 && group.isPast && (
                    <p className="text-sm text-muted-foreground italic">
                      Nothing scheduled.
                    </p>
                  )}
                  {canEdit && !group.isPast && (
                    <AddItemCard
                      onClick={() => {
                        setSelectedDate(new Date(group.dateKey + 'T12:00:00'));
                        setShowAddModal(true);
                      }}
                    />
                  )}
                </div>
              </section>
            ))}
            <div
              role="status"
              aria-live="polite"
              className="text-sm text-muted-foreground text-center min-h-[1.25rem]"
            >
              {isExtending ? 'Loading more…' : ''}
            </div>
            {/* Bottom sentinel — triggers an append when scrolled into
                view. Combined with `rootMargin: 400px 0px` inside the
                hook this prefetches a screen-ish ahead of the user. */}
            <div
              ref={bottomSentinelRef}
              aria-hidden="true"
              aria-busy={isExtending}
            />
          </div>
        )}
      </div>

      {showAddModal && (
        <ItemFormModal
          mode="create"
          // YAR-295: this was hardcoded `[]`, which silently hid the whole
          // "Related Event" field — ItemFormModal only renders it when there
          // is at least one event to pick (or one already linked). The loaded
          // schedule events are right here; Review was the only surface ever
          // passing them, so linking was impossible everywhere else.
          availableEvents={events.map((e) => ({ id: e.id, title: e.title }))}
          availableChildren={householdChildren.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          availableMembers={householdMembers
            .filter((m) => m.users)
            .map((m) => ({
              user_id: m.user_id,
              display_name: m.users!.display_name ?? m.users!.email ?? '',
              email: m.users!.email,
              avatar_emoji: m.users!.avatar_emoji,
              avatar_color: m.users!.avatar_color,
              avatar_image_path: m.users!.avatar_image_path,
            }))}
          onSubmit={handleAddItem}
          onBack={() => setShowAddModal(false)}
          defaultDate={selectedDateKey}
        />
      )}

      {detailItem && (
        <TaskEventDetailModal
          item={detailItem}
          canEdit={canEdit}
          timezone={userTimezone}
          relatedEventTitle={
            detailItem.kind === 'task' && detailItem.data.related_event_id
              ? events.find((e) => e.id === detailItem.data.related_event_id)
                  ?.title
              : undefined
          }
          onClose={() => setDetailItem(null)}
          onEdit={() => {
            setEditingItem(detailItem);
            setDetailItem(null);
          }}
          onToggleComplete={
            canComplete
              ? (id, status) =>
                  void completion.toggle({
                    id,
                    title: detailItem.data.title,
                    status,
                  })
              : undefined
          }
        />
      )}

      {editingItem && (
        <ItemFormModal
          mode="edit"
          initialType={editingItem.kind}
          initialData={
            editingItem.kind === 'task'
              ? taskToReviewItem(editingItem.data)
              : eventToReviewItem(editingItem.data)
          }
          // YAR-295. Exclude the item being edited: an event must not offer
          // itself as its own related event. (The field is task-only, so this
          // only matters if a future change ever surfaces it for events.)
          availableEvents={events
            .filter((e) => e.id !== editingItem.data.id)
            .map((e) => ({ id: e.id, title: e.title }))}
          availableChildren={householdChildren.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          availableMembers={householdMembers
            .filter((m) => m.users)
            .map((m) => ({
              user_id: m.user_id,
              display_name: m.users!.display_name ?? m.users!.email ?? '',
              email: m.users!.email,
              avatar_emoji: m.users!.avatar_emoji,
              avatar_color: m.users!.avatar_color,
              avatar_image_path: m.users!.avatar_image_path,
            }))}
          initialMemberIds={
            editingItem.kind === 'task'
              ? getMembersFromTask(editingItem.data).map((m) => m.id)
              : getMembersFromEvent(editingItem.data).map((m) => m.id)
          }
          onSubmit={handleEditItem}
          onBack={() => setEditingItem(null)}
          seriesDelete={buildSeriesDelete({
            event: editingItem.kind === 'event' ? editingItem.data : null,
            householdId,
            canDelete,
            onDeleted: async () => {
              setEditingItem(null);
              await refreshAfterMutation();
            },
          })}
          onDelete={
            canDelete
              ? async () => {
                  if (editingItem.kind === 'task') {
                    await scheduleService.deleteTask(editingItem.data.id);
                  } else {
                    await scheduleService.deleteEvent(editingItem.data.id);
                  }
                  setEditingItem(null);
                  await refreshAfterMutation();
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
